import { Box, Flex, useBreakpointValue } from '@spothero/ui';
import React, { useCallback } from 'react';
import { SearchComponentStoreProvider, SearchComponent, } from '@spothero/search-component';
import { useGoogleMapsLibraries } from 'common/google-map-context';
import { PoweredByGoogle } from '@spothero/ui-backlog';
import useConfig from 'hooks/use-config';
import { useDispatch, useSelector } from 'react-redux';
import { useReduxSearchState } from 'pages/search/hooks/use-redux-search-state';
import { SearchPageView } from 'utils/search-page-view';
import { useShowEventsData } from 'pages/search/hooks/use-show-events-data';
import { setSearchComponentEventView } from 'store/search/search-actions';
import { useDefaultSuggestion } from 'pages/search/hooks/use-default-suggestion';
import { mapReduxSearchStateToSearchComponentState } from 'pages/search/utils/state-mapping-utils';
import trackEventListItemSelected from 'segment/events/search-component/event-list-item-selected';
import trackSearchSuggestionSelected from 'segment/events/search-component/search-suggestion-selected';
import trackDateEntered from 'segment/events/search-component/date-entered';
import trackErrorMessageDisplayed from 'segment/events/search-component/error-message-displayed';
import trackPowerBookingOpened from 'segment/events/search-component/power-booking-opened';
import trackFindParkingNearMeClicked from 'segment/events/search-component/find-parking-near-me-clicked';
import trackSwitchedVerticalTabs from 'segment/events/search-component/switched-vertical-tabs';
import { useTooltips, TOOLTIPS } from 'pages/search/hooks/use-tooltips';
import { updateSearchComponentKey } from 'store/search-request/search-request-actions';
import { applySearchUpdates } from 'pages/search/hooks/use-search-updates';
import { useHistory } from 'react-router';
import { hasSearchChanged } from 'pages/search/utils/has-search-changed';
import { MobileSearchComponent } from './mobile-search-component/MobileSearchComponent';
import { SEARCH_WIDGET_TOOLTIP_DARK_STYLE_OVERRIDE } from 'pages/search/utils/style/style-overrides';
// eslint-disable-next-line no-empty-function
const handler = () => { };
const spotListMobileListViewStyles = {};
export const SearchComponentContainer = () => {
    const history = useHistory();
    const { isVisible: isEventOffsetTooltipVisible, text: eventTooltipText, handleClose: handleTooltipClose, } = useTooltips(TOOLTIPS.event_offset_popover);
    const { showSEMEvent } = useShowEventsData();
    const dispatch = useDispatch();
    const searchOnClick = () => dispatch(setSearchComponentEventView('selectWFilter'));
    const isOpen = useSelector((state) => state.search.data.searchComponentIsOpen);
    const searchComponentKey = useSelector((state) => state.searchRequest.searchComponentKey);
    const isSpotDetails = useSelector((state) => state.spots.selectedSpotId);
    const { googleMapsLoaded, googleMapsLibraries, useNewGooglePlacesApi, } = useGoogleMapsLibraries();
    const { siteUrl } = useConfig();
    const isMobile = useBreakpointValue({ base: true, tablet: false }, { ssr: false });
    const isTablet = useBreakpointValue({ base: false, tablet: true, desktop: false }, { ssr: false });
    const defaultSuggestion = useDefaultSuggestion();
    const reduxState = useReduxSearchState();
    const searchComponentState = mapReduxSearchStateToSearchComponentState({
        parkingType: reduxState.parkingType,
        starts: reduxState.startsDate,
        ends: reduxState.endsDate,
        powerBookingSource: reduxState.powerBookingSource,
        powerBookingPeriods: reduxState.powerBookingPeriods,
    });
    const handleTrackVerticalTabs = useCallback(({ previousTab, selectedTab, }) => {
        trackSwitchedVerticalTabs({
            pageType: 'homepage',
            previousTab,
            selectedTab,
        });
    }, []);
    const onDateEntered = useCallback(async (params, partialStore) => {
        if (!isMobile) {
            await applySearchUpdates({
                selectedSuggestion: partialStore?.defaultSuggestion,
                reduxSearchState: reduxState,
                findParkingPartialStore: partialStore,
                googleMapsLibraries,
                setIsLoaderShowing: () => { },
                closeSearchComponent: () => { },
                dispatch,
                history,
            });
        }
        trackDateEntered(params);
    }, [dispatch, googleMapsLibraries, history, isMobile, reduxState]);
    const onBlur = useCallback(async (partialStore) => {
        // only run blur on desktop
        if (isMobile) {
            return;
        }
        if (!partialStore) {
            return;
        }
        const isDateTimePickerInvalid = partialStore?.parkingSubType === 'power_booking' &&
            partialStore?.errorType;
        const isHourlyMonthlyPowerBooking = partialStore?.parkingSubType === 'hourly' ||
            partialStore?.parkingSubType === 'monthly' ||
            partialStore?.parkingSubType === 'power_booking';
        const isLocationInvalid = isHourlyMonthlyPowerBooking
            ? !partialStore.selectedSuggestion
            : false; // consider location valid if not hourly, monthly or power booking
        // Restore the original search component state when it's in an invalid state
        if (isDateTimePickerInvalid || isLocationInvalid) {
            dispatch(updateSearchComponentKey());
            return;
        }
        const hasChanged = hasSearchChanged(partialStore, searchComponentState);
        if (hasChanged) {
            await applySearchUpdates({
                selectedSuggestion: partialStore?.defaultSuggestion,
                reduxSearchState: reduxState,
                findParkingPartialStore: partialStore,
                googleMapsLibraries,
                setIsLoaderShowing: () => { },
                closeSearchComponent: () => { },
                dispatch,
                history,
            });
        }
    }, [
        dispatch,
        googleMapsLibraries,
        history,
        isMobile,
        reduxState,
        searchComponentState,
    ]);
    const onSearchSuggestionSelected = useCallback(async (params, partialStore) => {
        if (!isMobile) {
            await applySearchUpdates({
                selectedSuggestion: params,
                reduxSearchState: reduxState,
                findParkingPartialStore: partialStore,
                googleMapsLibraries,
                setIsLoaderShowing: () => { },
                closeSearchComponent: () => { },
                dispatch,
                history,
            });
        }
        trackSearchSuggestionSelected(params);
    }, [dispatch, googleMapsLibraries, history, isMobile, reduxState]);
    const onParkingChange = useCallback(async (partialStore) => {
        if (!isMobile) {
            await applySearchUpdates({
                selectedSuggestion: partialStore?.defaultSuggestion,
                reduxSearchState: reduxState,
                findParkingPartialStore: partialStore,
                googleMapsLibraries,
                setIsLoaderShowing: () => { },
                closeSearchComponent: () => { },
                dispatch,
                history,
            });
        }
    }, [dispatch, googleMapsLibraries, history, isMobile, reduxState]);
    const clientRendered = {
        googleMapsLibraries,
        searchEvents: [],
        handleOnChangeForEvents: handler,
        isMobile,
        isTablet,
        timezone: reduxState.timezone,
        useNewGooglePlacesApi,
        dateTimeTooltipText: isEventOffsetTooltipVisible
            ? eventTooltipText
            : '',
        onDateTimeTooltipClose: () => {
            handleTooltipClose();
        },
    };
    const providerProps = {
        siteUrl,
        eventDestinationsHash: {},
        locationBias: {},
        airports: reduxState.airports,
        parkingType: searchComponentState.parkingType,
        parkingSubType: searchComponentState.parkingSubType,
        startDate: searchComponentState.startDate,
        endDate: searchComponentState.endDate,
        monthlyStartDate: searchComponentState.monthlyStartDate,
        dates: searchComponentState.powerBookingDates,
        startTime: searchComponentState.powerBookingStartTime,
        endTime: searchComponentState.powerBookingEndTime,
        // Below event key is used for events specific to the destination in redux.  They are prepopulated and static.
        events: Array.isArray(reduxState.events)
            ? reduxState.events.map(event => ({
                ...event,
                // Below two keys help to support session storage for recent searches
                venue: reduxState.destination.title,
                location: {
                    latitude: reduxState.destination.latitude,
                    longitude: reduxState.destination.longitude,
                },
            }))
            : [],
        // https://github.com/spothero/fe-monorepo/blob/f7be629a079b49b8a056af5d85316b9442a0acae/packages/consumer/search-component/src/search-widget/store/common.tsx#L157
        eventPanel: {
            searchOnClick,
        },
        defaultSuggestion,
        trackEventListItemSelected,
        trackSearchSuggestionSelected: onSearchSuggestionSelected,
        trackDateEntered: onDateEntered,
        trackErrorMessageDisplayed,
        trackFindParkingNearMeClicked,
        trackSwitchedVerticalTabs: handleTrackVerticalTabs,
        trackPowerBookingOpened,
        PoweredByGoogle,
        dateTimePickerButtonLabel: 'Submit',
        onBlur,
        onParkingChange,
    };
    if (!googleMapsLoaded) {
        return <div data-testid="failed-google-maps-load"/>;
    }
    const styles = reduxState.view === SearchPageView.MOBILE_LIST
        ? spotListMobileListViewStyles
        : {};
    return (<Flex justifyContent="center" alignItems="center" backgroundColor="white" sx={{
            ...(isMobile
                ? {}
                : {
                    '& .SearchWidget': {
                        justifyContent: 'center',
                        alignItems: 'center',
                    },
                }),
            '& .SearchWidget > div': {
                marginTop: '0', // Clear extra margins from SearchWidget elements
            },
            ...styles,
        }} width={isMobile ? '100%' : 'auto'} position="relative" data-testid="SearchComponentContainer" className="SearchComponentContainer">
            <Box flexGrow={1} overflowX={isMobile ? 'clip' : 'visible'} zIndex={isSpotDetails ? 0 : 'layer5'} // 2 more than .SpotMap css - .Search-map-view .SpotList, .ResponsiveSearchLayout-zone-2 .SpotList
     backgroundColor="white" paddingTop={isMobile ? 4 : 0} paddingBottom={(!isOpen && !showSEMEvent) || !isMobile ? 0 : 4} // Moving this padding to filter selection for this case to support a better scroll bar
     key={searchComponentKey} data-testid="SearchComponentContainer-Box" maxWidth="100vw" sx={{
            '& .SearchWidgetTooltip': SEARCH_WIDGET_TOOLTIP_DARK_STYLE_OVERRIDE,
        }}>
                <SearchComponentStoreProvider locationId={undefined} locationKind={undefined} useNewGooglePlacesApi={useNewGooglePlacesApi} clientRendered={clientRendered} eventMode="search" eventPanel {...providerProps}>
                    {isMobile ? <MobileSearchComponent /> : <SearchComponent />}
                </SearchComponentStoreProvider>
            </Box>
            {isOpen && isMobile && (<Box position="absolute" backgroundColor="navy.900" opacity=".3" height="100vh" width="100vw" top="0" bottom="0" left="0" right="0" zIndex="layer4" // 1 more than css .SpotMap - .Search-map-view .SpotList, .ResponsiveSearchLayout-zone-2 .SpotList
        />)}
        </Flex>);
};
